/**
 * Workflow Designer Component
 * Visual workflow builder with drag-and-drop step reordering
 * Allows creating and editing multi-step Volcano workflows
 */

import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Plus,
  Trash2,
  Copy,
  GripVertical,
  Save,
  X,
  AlertTriangle,
} from 'lucide-react';
import type { VolcanoStepTemplate, VolcanoWorkflow, WorkflowStep, Provider } from '../types/volcano.types';
import { estimateStepCost, formatCost } from '../utils/cost-calculator';
import { getProviderColor, getProviderName, getDefaultModel } from '../utils/provider-config';
import StepEditor from './StepEditor';
import StepLibraryView from './steps/StepLibraryView';

interface WorkflowDesignerProps {
  workflow: VolcanoWorkflow;
  agentId: string;
  onSave: (workflow: VolcanoWorkflow) => Promise<void>;
  onCancel: () => void;
}

export default function WorkflowDesigner({
  workflow: initialWorkflow,
  agentId,
  onSave,
  onCancel,
}: WorkflowDesignerProps) {
  const [workflow, setWorkflow] = useState<VolcanoWorkflow>(initialWorkflow);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  const [showLibrary, setShowLibrary] = useState(false);

  // Handle drag end
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const steps = Array.from(workflow.steps);
    const [reorderedStep] = steps.splice(result.source.index, 1);
    steps.splice(result.destination.index, 0, reorderedStep);

    setWorkflow({ ...workflow, steps });
    setIsDirty(true);
  };

  // Add new step
  const handleAddStep = () => {
    const newStep: WorkflowStep = {
      name: `Step ${workflow.steps.length + 1}`,
      provider: 'anthropic',
      model: getDefaultModel('anthropic'),
      prompt: 'Enter your prompt here...',
      mcps: [],
      timeout: 30000,
      retry: {
        strategy: 'exponential',
        attempts: 3,
      },
    };

    setWorkflow({
      ...workflow,
      steps: [...workflow.steps, newStep],
    });
    setIsDirty(true);
  };

  // Duplicate step
  const handleDuplicateStep = (index: number) => {
    const stepToDuplicate = workflow.steps[index];
    const duplicatedStep: WorkflowStep = {
      ...stepToDuplicate,
      name: `${stepToDuplicate.name || `Step ${index + 1}`} (Copy)`,
    };

    const steps = [...workflow.steps];
    steps.splice(index + 1, 0, duplicatedStep);

    setWorkflow({ ...workflow, steps });
    setIsDirty(true);
  };

  // Delete step
  const handleDeleteStep = (index: number) => {
    if (workflow.steps.length === 1) {
      setError('Workflow must have at least one step');
      return;
    }

    if (!confirm('Are you sure you want to delete this step?')) return;

    const steps = workflow.steps.filter((_, i) => i !== index);
    setWorkflow({ ...workflow, steps });
    setIsDirty(true);
  };

  // Edit step
  const handleEditStep = (index: number) => {
    setEditingStepIndex(index);
  };

  // Save edited step
  const handleSaveStep = async (updatedStep: WorkflowStep) => {
    if (editingStepIndex === null) return;

    const steps = [...workflow.steps];
    steps[editingStepIndex] = updatedStep;

    setWorkflow({ ...workflow, steps });
    setIsDirty(true);
    setEditingStepIndex(null);
  };

  // Validate workflow
  const validateWorkflow = (): string | null => {
    if (workflow.steps.length === 0) {
      return 'Workflow must have at least one step';
    }

    const stepNames = workflow.steps.map((s) => s.name?.trim() || '').filter(Boolean);
    const duplicates = stepNames.filter((name, index) => stepNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      return `Duplicate step names found: ${duplicates.join(', ')}`;
    }

    for (const step of workflow.steps) {
      const isLlm = step.provider === 'anthropic' || step.provider === 'openai';
      if (isLlm && (!step.prompt || String(step.prompt).trim() === '')) {
        return 'All LLM steps must have a prompt';
      }
      if (step.timeout != null && Number(step.timeout) <= 0) {
        return 'All steps must have a timeout greater than 0';
      }
      if (step.retry && Number(step.retry.attempts) <= 0) {
        return 'Retry attempts must be at least 1';
      }
    }

    return null;
  };

  const handleInsertTemplate = (template: VolcanoStepTemplate) => {
    const step: WorkflowStep = {
      ...(template.step || {}),
      name: (template.step as any)?.name || template.name,
      provider: (template.step as any)?.provider || 'anthropic',
      stepTemplateId: template.id,
      stepTemplateVersion: template.version,
      stepTemplateNodeType: template.node_type,
    };

    setWorkflow((prev) => ({
      ...prev,
      steps: [...prev.steps, step],
    }));
    setIsDirty(true);
    setShowLibrary(false);
  };

  // Save workflow
  const handleSave = async () => {
    setError(null);

    const validationError = validateWorkflow();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      await onSave(workflow);
      setIsDirty(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancelClick = () => {
    if (isDirty && !confirm('You have unsaved changes. Are you sure you want to cancel?')) {
      return;
    }
    onCancel();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b p-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Workflow Designer</h2>
          <p className="text-sm text-gray-600">
            {workflow.steps.length} step{workflow.steps.length !== 1 ? 's' : ''}
            {isDirty && <span className="text-orange-600 ml-2">• Unsaved changes</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCancelClick} disabled={saving}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !isDirty}>
            {saving ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Workflow
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="m-4">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* Step Palette */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-blue-900">Add Steps</h3>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowLibrary((v) => !v)}>
                  From Library
                </Button>
                <Button size="sm" onClick={handleAddStep}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Step
                </Button>
              </div>
            </div>
            <p className="text-sm text-blue-700">
              Create a new step or drag existing steps to reorder them
            </p>
          </div>

          {showLibrary && (
            <div className="border rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold">Insert from Volcano Step Library</div>
                <Button size="sm" variant="outline" onClick={() => setShowLibrary(false)}>
                  Close
                </Button>
              </div>
              <StepLibraryView onInsert={handleInsertTemplate} />
            </div>
          )}

          {/* Workflow Steps */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="workflow-steps">
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={`space-y-3 min-h-[200px] ${
                    snapshot.isDraggingOver ? 'bg-blue-50 rounded-lg' : ''
                  }`}
                >
                  {workflow.steps.map((step, index) => (
                    <Draggable
                      key={`step-${index}`}
                      draggableId={`step-${index}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`${
                            snapshot.isDragging ? 'shadow-xl border-blue-400' : ''
                          }`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              {/* Drag Handle */}
                              <div
                                {...provided.dragHandleProps}
                                className="cursor-grab active:cursor-grabbing mt-1"
                              >
                                <GripVertical className="w-5 h-5 text-gray-400" />
                              </div>

                              {/* Step Number */}
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                                style={{
                                  backgroundColor: getProviderColor(step.provider),
                                }}
                              >
                                {index + 1}
                              </div>

                              {/* Step Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900">
                                      {step.name || `Step ${index + 1}`}
                                    </span>
                                    <Badge
                                      variant="outline"
                                      style={{
                                        backgroundColor: `${getProviderColor(
                                          step.provider
                                        )}20`,
                                        color: getProviderColor(step.provider),
                                        borderColor: getProviderColor(step.provider),
                                      }}
                                    >
                                      {getProviderName(step.provider)}
                                    </Badge>
                                  </div>
                                  <Badge variant="secondary" className="font-mono">
                                    {formatCost(estimateStepCost(step).avg)}
                                  </Badge>
                                </div>

                                <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                  {step.prompt
                                    ? `${String(step.prompt).substring(0, 100)}${
                                        String(step.prompt).length > 100 ? '...' : ''
                                      }`
                                    : JSON.stringify(step).substring(0, 100) + '...'}
                                </p>

                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  {step.timeout != null && <span>Timeout: {Number(step.timeout) / 1000}s</span>}
                                  {step.retry && (
                                    <>
                                      <span>•</span>
                                      <span>
                                        Retry: {step.retry.attempts}x {step.retry.strategy}
                                      </span>
                                    </>
                                  )}
                                  {(step.mcps || []).length > 0 && (
                                    <>
                                      <span>•</span>
                                      <span>{(step.mcps || []).length} MCP tool(s)</span>
                                    </>
                                  )}
                                  {step.stepTemplateId && (
                                    <>
                                      <span>•</span>
                                      <span className="font-mono">template:{step.stepTemplateId}</span>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditStep(index)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDuplicateStep(index)}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteStep(index)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {/* Empty State */}
          {workflow.steps.length === 0 && (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-600 mb-4">No steps in this workflow</p>
              <Button onClick={handleAddStep}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Step
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Step Editor Modal */}
      {editingStepIndex !== null && (
        <StepEditor
          step={workflow.steps[editingStepIndex]}
          stepIndex={editingStepIndex}
          agentId={agentId}
          isOpen={true}
          onSave={handleSaveStep}
          onCancel={() => setEditingStepIndex(null)}
        />
      )}
    </div>
  );
}

